import { takeLatest, call, put, all, select } from 'redux-saga/effects';
import { PowertrainApiV2 } from 'api';
import { apiCallStatus } from 'helpers/constants';
import logger from 'helpers/logger';
import { translate } from 'helpers/translateHelper';
import { notify } from 'sharedComponents/GeneralNotification';
import { NOTIFICATION_TYPES } from 'sharedComponents/GeneralNotification/constants';
import { actionTypes, DUPLICATE_ASSET_SITE_ERROR_MESSAGE_KEY } from './constants';
import {
    getPowertrainsRequest,
    reloadOverviewPowertrainsRequest,
    getPowertrainsAction,
    deletePowertrainAction,
    convertToLocationAction,
    getHierarchyDataAction,
    updateHierarchyDataAction,
    getHierarchyDataRequest,
    setSelectedHierarchyNodes,
    setSelectedPowertrain,
    setConversionResult
} from './actions';
import {
    formatGetPowertrainsRequest,
} from './helpers';
import { cleanTempLocationIds } from './Hierarchy/helpers';
import { NODE_TYPE } from './Hierarchy/constants';
import { setPowertrainUpdateStatus } from '../../PowertrainConditionMonitoring/ConditionIndicesOverviewV2/actions';
import { getExistingSiteIds } from 'helpers/sitesHelper';


// Initialize data by loading both hierarchy and powertrains in parallel
function* initializeDataSaga({ organizationId, siteIds, organizationIds }) {
    yield put(getHierarchyDataAction(apiCallStatus.LOADING));
    yield put(getPowertrainsAction(apiCallStatus.LOADING));
    try {
        const [hierarchyData, powertrains] = yield all([
            call(PowertrainApiV2.getHierarchyData, organizationId, siteIds),
            call(
                PowertrainApiV2.getPowertrains,
                formatGetPowertrainsRequest(siteIds, organizationIds, '', 1)
            )
        ]);

        yield put(getHierarchyDataAction(apiCallStatus.SUCCESS, { hierarchyData }));
        yield put(getPowertrainsAction(apiCallStatus.SUCCESS, { powertrains, pageNumber: 1 }));

        const state = yield select();
        const conversionResult = state?.powertrainTools?.configurationV2?.conversionResult;
        const selectedPowertrain = state?.powertrainTools?.configurationV2?.selectedPowertrain;

        // After conversion, auto-select the single assets node for the newly created location
        if (conversionResult?.locationId && conversionResult?.showBanner) {
            const singleAssetsNodeId = `singleasset-location-${conversionResult.locationId}`;
            yield put(setSelectedHierarchyNodes([singleAssetsNodeId]));
        } else if (selectedPowertrain?.powertrainId) {
            // Preserve selection if a powertrain was just created/edited
            yield put(setSelectedHierarchyNodes([selectedPowertrain.powertrainId]));
        } else {
            yield put(setSelectedHierarchyNodes([]));
        }

        logger.debug('Hierarchy and powertrains data successfully loaded in parallel');
    } catch (err) {
        yield put(getHierarchyDataAction(apiCallStatus.ERROR, [], err));
        yield put(getPowertrainsAction(apiCallStatus.ERROR, [], err));
        logger.error('Error loading initial data:', err);
    }
}


function* getPowertrainsSaga({ siteIds, organizationIds, nameFilter, pageNumber }) {
    yield put(getPowertrainsAction(apiCallStatus.LOADING));
    try {
        const powertrains = yield call(
            PowertrainApiV2.getPowertrains,
            formatGetPowertrainsRequest(siteIds, organizationIds, nameFilter, pageNumber)
        );
        yield put(getPowertrainsAction(apiCallStatus.SUCCESS, { powertrains, pageNumber }));
        logger.debug('Powertrains successfully loaded for page ' + pageNumber);
    } catch (err) {
        yield put(getPowertrainsAction(apiCallStatus.ERROR, [], err));
    }
}

function* deletePowertrainSaga({ powertrainId, organizationIds, siteIds, nameFilter }) {
    yield put(deletePowertrainAction(apiCallStatus.LOADING));
    try {
        yield call(PowertrainApiV2.deletePowertrain, powertrainId);
        yield put(deletePowertrainAction(apiCallStatus.SUCCESS, { powertrainId }));
        yield put(getPowertrainsRequest(siteIds, organizationIds, nameFilter, 1));

        // Show success notification
        notify(translate('ABB.Powertrain.Frontend.powertrainDeleteSuccessMessage'), NOTIFICATION_TYPES.SUCCESS);
        logger.debug('Powertrain deleted successfully');

        //TO DO: edit when common powertrain list is used for configuration and overview
        yield put(reloadOverviewPowertrainsRequest());
        yield put(getHierarchyDataRequest(organizationIds[0], siteIds));
        yield put(setPowertrainUpdateStatus(true));
    } catch (err) {
        yield put(deletePowertrainAction(apiCallStatus.ERROR, [], err));

        // Show error notification
        const errorMessage = err?.message || translate('ABB.Powertrain.Frontend.powertrainDeleteErrorMessage');
        notify(errorMessage, NOTIFICATION_TYPES.ERROR);
        logger.error('Error deleting powertrain:', err);
    }
}

const findSiteRowVersion = (hierarchyDataOrg, siteId) => {
    const searchOrg = (org) => {
        if (!org || typeof org !== 'object') { return undefined; }
        const site = (org.sites || []).find(s => s.siteId === siteId);
        if (site) { return site?.rowVersion; }
        for (const subOrg of org.subOrganizations || []) {
            const rv = searchOrg(subOrg);
            if (rv !== undefined) { return rv; }
        }
        return undefined;
    };
    return searchOrg(hierarchyDataOrg) ?? null;
};

const getHierarchyUpdateSuccessMessageKey = (moveContext) => {
    if (moveContext?.type === NODE_TYPE.POWERTRAIN) {
        return moveContext.count > 1
            ? 'ABB.Powertrain.Frontend.powertrainsMovedSuccessfully'
            : 'ABB.Powertrain.Frontend.powertrainMovedSuccessfully';
    }

    if (moveContext?.type === NODE_TYPE.UNASSIGNED_ASSETS) {
        return moveContext.count > 1
            ? 'ABB.Powertrain.Frontend.assetsMovedSuccessfully'
            : 'ABB.Powertrain.Frontend.assetMovedSuccessfully';
    }

    return 'ABB.Powertrain.Frontend.hierarchyUpdatedSuccessfully';
};

const getHierarchyUpdateSuccessMessage = (moveContext) => {
    return translate(getHierarchyUpdateSuccessMessageKey(moveContext));
};

const getHierarchyUpdateStatusCode = (err) => err?.response?.status || err?.status;

const isConflictError = (statusCode) => statusCode === 409;

const getHierarchyUpdateRequestPayload = (err) => {
    return typeof err?.config?.data === 'string'
        ? JSON.parse(err.config.data)
        : err?.config?.data;
};

const shouldUseSiteNameInErrorMessage = (statusCode, errorMessage) => {
    return statusCode === 400 &&
        errorMessage?.toLowerCase().includes(DUPLICATE_ASSET_SITE_ERROR_MESSAGE_KEY);
};

const replaceSitePlaceholderInErrorMessage = (errorMessage, err, nodeMap) => {
    const requestPayload = getHierarchyUpdateRequestPayload(err);
    const siteName = nodeMap?.[requestPayload?.siteId]?.name;

    return siteName
        ? errorMessage.replaceAll(new RegExp(String.raw`\b${DUPLICATE_ASSET_SITE_ERROR_MESSAGE_KEY}\b`, 'gi'), siteName)
        : errorMessage;
};

const getHierarchyUpdateErrorMessage = (err, statusCode, nodeMap) => {
    const errorMessage = err?.response?.data?.message || err?.message;

    if (shouldUseSiteNameInErrorMessage(statusCode, errorMessage)) {
        return replaceSitePlaceholderInErrorMessage(errorMessage, err, nodeMap);
    }

    return errorMessage;
};

function* convertToLocationSaga({ powertrainId, siteId, organizationId }) {
    yield put(convertToLocationAction(apiCallStatus.LOADING));
    try {
        const state = yield select();
        const hierarchyData = state?.powertrainTools?.configurationV2?.hierarchyData?.data?.hierarchyData;
        const siteRowVersion = findSiteRowVersion(hierarchyData, siteId);
        const response = yield call(PowertrainApiV2.convertToLocation, powertrainId, siteRowVersion);
        yield put(convertToLocationAction(apiCallStatus.SUCCESS));
        yield put(setSelectedPowertrain(null));
        yield put(setSelectedHierarchyNodes([]));

        // Store conversion result to show banner and auto-select after hierarchy reload
        yield put(setConversionResult({
            siteId: response?.siteId,
            locationId: response?.locationId,
            locationName: response?.locationName,
            showBanner: true
        }));

        const selectedFilters = state?.generalFilter?.selectedFilters;
        const siteIds = getExistingSiteIds(selectedFilters?.siteIds) || null;
        yield put(getHierarchyDataRequest(organizationId, siteIds));
        yield put(reloadOverviewPowertrainsRequest());
        yield put(setPowertrainUpdateStatus(true));

        notify(translate('ABB.Powertrain.Frontend.powertrainConvertedToLocationSuccess'), NOTIFICATION_TYPES.SUCCESS);
        logger.debug('Powertrain converted to location successfully');
    } catch (err) {
        yield put(convertToLocationAction(apiCallStatus.ERROR, [], err));

        const errorMessage = err?.message || translate('ABB.Powertrain.Frontend.convertToLocationErrorMessage');
        notify(errorMessage, NOTIFICATION_TYPES.ERROR);
        logger.error('Error converting powertrain to location:', err);
    }
}

function* getHierarchyDataSaga({ organizationId, siteIds }) {
    yield put(getHierarchyDataAction(apiCallStatus.LOADING));
    try {
        const hierarchyData = yield call(PowertrainApiV2.getHierarchyData, organizationId, siteIds);
        yield put(getHierarchyDataAction(apiCallStatus.SUCCESS, { hierarchyData }));

        const state = yield select();
        const conversionResult = state?.powertrainTools?.configurationV2?.conversionResult;
        const selectedPowertrain = state?.powertrainTools?.configurationV2?.selectedPowertrain;

        // After conversion, auto-select the single assets node for the newly created location
        if (conversionResult?.locationId && conversionResult?.showBanner) {
            const singleAssetsNodeId = `singleasset-location-${conversionResult.locationId}`;
            yield put(setSelectedHierarchyNodes([singleAssetsNodeId]));
        } else if (selectedPowertrain?.powertrainId) {
            // Preserve selection if a powertrain was just created/edited
            yield put(setSelectedHierarchyNodes([selectedPowertrain.powertrainId]));
        } else {
            yield put(setSelectedHierarchyNodes([]));
        }
        logger.debug('Hierarchy data successfully loaded');
    } catch (err) {
        yield put(getHierarchyDataAction(apiCallStatus.ERROR, [], err));
        logger.error('Error loading hierarchy data:', err);
    }
}

function* updateHierarchyDataSaga({
    hierarchyRequestData,
    callback,
    setHierarchyErrorModal,
    setSaveFailed,
    moveContext,
    nodeMap
}) {
    yield put(updateHierarchyDataAction(apiCallStatus.LOADING));
    try {
        hierarchyRequestData.forEach(site => {
            cleanTempLocationIds(site.locations);
        });
        const updateHierarchy = hierarchyRequestData?.map(site => call(PowertrainApiV2.updateHierarchyData, site, site.siteId));
        yield all(updateHierarchy);
        yield put(updateHierarchyDataAction(apiCallStatus.SUCCESS));

        notify(getHierarchyUpdateSuccessMessage(moveContext), NOTIFICATION_TYPES.SUCCESS);
        logger.debug('Hierarchy data successfully updated');
        yield put(setPowertrainUpdateStatus(true));
        if (callback) {
            callback();
        }
    } catch (err) {
        const statusCode = getHierarchyUpdateStatusCode(err);
        if (isConflictError(statusCode)) {
            setHierarchyErrorModal(true);
            setSaveFailed(true);
        }
        yield put(updateHierarchyDataAction(apiCallStatus.ERROR, [], err));
        if (!isConflictError(statusCode)) {
            const errorMessage = getHierarchyUpdateErrorMessage(err, statusCode, nodeMap);
            notify(errorMessage || translate('ABB.Powertrain.Frontend.hierarchyUpdateErrorMessage'), NOTIFICATION_TYPES.ERROR);
        }
        logger.error('Error updating hierarchy data:', err);
    }
}

export function* powertrainConfigurationWatchers() {
    yield takeLatest(actionTypes.INITIALIZE_DATA_REQUEST, initializeDataSaga);
    yield takeLatest(actionTypes.LOAD_POWERTRAINS_REQUEST, getPowertrainsSaga);
    yield takeLatest(actionTypes.DELETE_POWERTRAIN_REQUEST, deletePowertrainSaga);
    yield takeLatest(actionTypes.CONVERT_TO_LOCATION_REQUEST, convertToLocationSaga);
    yield takeLatest(actionTypes.GET_HIERARCHY_DATA_REQUEST, getHierarchyDataSaga);
    yield takeLatest(actionTypes.UPDATE_HIERARCHY_DATA_REQUEST, updateHierarchyDataSaga);
}
